import { useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import ReactDOM from 'react-dom/client';
import { Quill } from 'react-quill';
import { Provider } from 'react-redux';
import store from 'redux/store';
import {
  setIsOpen,
  setIsExpanded,
  setCurrentRange,
  setCurrentBounds,
  setIsDisplayed,
  closeToolbar,
  summarizeText,
  continueWriting,
  setIsShortcutOpen,
  setSelectedText,
  restartCurrentAction,
  stopGenerate,
} from 'redux/toolbar';
import { setIsOpen as setAutocompleteIsOpen } from 'redux/autocomplete';
import ToolbarEditor from 'components/ToolbarEditor';
import { getOptionByKey } from 'components/ToolbarOptions';
import lizzyApi from 'lizzyApiAdapter';

const Module = Quill.import('core/module');

export default class EditorLizzyToolbarModule extends Module {
  constructor(quill, options) {
    super(quill, options);

    this.quill = quill;
    this.options = options;
    this.lizzy = null;

    this.handleClickInside = this.handleClickInside.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSelection = this.handleSelection.bind(this);
    this.handleWidgetClick = this.handleWidgetClick.bind(this);
    this.handleContractOverview = this.handleContractOverview.bind(this);
    this.showToolbar = debounce(this.showToolbar.bind(this), 300);
  }

  onMount(lizzy) {
    this.lizzy = lizzyApi;
    this.popup = document.createElement('div');
    this.selectionPlaceholder = document.createElement('div');
    
    this.quill.container.appendChild(this.popup);
    this.quill.container.appendChild(this.selectionPlaceholder);

    this.currentRange = null;
    this.selectionRange = null;
    this.isPopupOpened = false;
    this.reactRoot = null;

    this.quill.on('selection-change', this.handleSelection);
    document.addEventListener('keydown', this.handleKeydown);

    this.lizzy.on('widgetClick', this.handleWidgetClick);
    this.lizzy.on('widgetClick:contract-overview', this.handleContractOverview);
  }

  onUnmount() {
    this.popup.remove();
    this.selectionPlaceholder.remove();

    this.quill.off('selection-change', this.handleSelection);
    this.lizzy.off('widgetClick', this.handleWidgetClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  get isSidebarOpen() {
    return store.getState().toolbar.itemStatus['sidebar'].active;
  }

  get isDisplayed() {
    return store.getState().toolbar.isDisplayed;
  }

  set isDisplayed(value) {
    store.dispatch(setIsDisplayed(value));
  }

  get isPopupOpened() {
    return store.getState().toolbar.isOpen;
  }

  set isPopupOpened(value) {
    store.dispatch(setIsOpen(value));
  }

  get currentRange() {
    return store.getState().toolbar.currentRange;
  }

  get hasChanges() {
    return store.getState().toolbar.hasChanges;
  }

  set currentRange(value) {
    if (value) {
      store.dispatch(setCurrentRange({
        index: value.index,
        length: value.length
      }));
    }
  }

  get currentBounds() {
    return store.getState().toolbar.currentBounds;
  }

  set currentBounds(value) {
    store.dispatch(setCurrentBounds(value));
  }

  get selectedText() {
    return store.getState().toolbar.selectedText;
  }

  set selectedText(value) {
    store.dispatch(setSelectedText(value));
  }

  get isWriting() {
    return store.getState().toolbar.isWriting;
  }

  setSelectionPlaceholder() {
    this.selectionPlaceholder.classList.add('selection-placeholder');
    this.selectionPlaceholder.style.position = 'absolute';
    this.selectionPlaceholder.style.left = 0;
    this.selectionPlaceholder.style.backgroundColor = '#ACCEF7';
    this.selectionPlaceholder.style.width = '100%';
    this.selectionPlaceholder.style.display = 'block';
  }

  handleSelection(range, oldRange, source) {
    if (this.isSidebarOpen) {
      return null;
    }

    this.currentRange = range;

    if (range && range.length > 0) {
      this.selectionRange = range;
      // create and show the popup
      // check if text is currently selected
      const text = this.quill.getText(range.index, range.length);

      this.selectedText = text;

      if (text) {
        this.showToolbar({ selectedText: text });
      }
    }
  }

  showToolbar({ optionKey, selectedText } = {}) {
    this.hideToolbarTimeout && clearTimeout(this.hideToolbarTimeout);
    this.isPopupOpenedTimeout && clearTimeout(this.isPopupOpenedTimeout);

    this.currentBounds = this.quill.getBounds(this.currentRange.index + this.currentRange.length);

    // It should be wrapped again into the <Provider> component
    // as it's another part of the current react-app
    const ToolbarComponent = () => {
      this.toolbarRef = useRef();
      const toolbarRef = this.toolbarRef;

      useEffect(() => {
        if (optionKey && toolbarRef.current) {
          toolbarRef.current.executeOptionHandler(getOptionByKey(optionKey));
        }
      }, [toolbarRef]);

      return (
        <Provider store={store}>
          <ToolbarEditor
            ref={this.toolbarRef}
            selectedText={selectedText}
            editorContainerEl={this.quill.container}
            onRestoreSelection={this.handleRestoreSelection.bind(this)}
            onClickOutside={this.handleClickOutside.bind(this)}
            onOptionClick={this.handleOptionClick.bind(this)}
            onInsertSuggestion={this.handleInsertSuggestion.bind(this)}
          />
        </Provider>
      );
    };

    if (!this.reactRoot) {
      this.reactRoot = ReactDOM.createRoot(this.popup);
    }
    this.reactRoot.render(<ToolbarComponent />);
    this.popup.addEventListener('click', this.handleClickInside)

    this.setSelectionPlaceholder();
    this.closeAutocomplete();

    this.isDisplayed = true;
    this.setFakeSelection(this.currentRange);

    this.isPopupOpenedTimeout = setTimeout(() => {
      this.isPopupOpened = true;
    }, 200);
  }

  hideToolbar() {
    this.hideToolbarTimeout = setTimeout(() => {
      this.reactRoot && this.reactRoot.unmount();
      this.reactRoot = null;
    }, 200);

    this.popup.removeEventListener('click', this.handleClickInside);
    this.setFakeSelection();
    store.dispatch(closeToolbar());
  }

  expandPopup() {
    this.currentBounds = this.quill.getBounds(this.currentRange.index + this.currentRange.length);
    store.dispatch(setIsExpanded(true));
  }

  handleClickInside() {
    this.setFakeSelection(this.currentRange);
  }

  handleClickOutside() {
    this.prevRange = { ...this.currentRange };
    this.hideToolbar();
  }

  setFakeSelection(range) {
    if (range && range.index !== undefined && range.length !== undefined) {
      const start = this.quill.getBounds(range.index);
      const end = this.quill.getBounds(range.index + range.length);

      const startTop = start.top;
      const startBottom = start.bottom;
      const startLeft = start.left;
      const startHeight = start.height;

      const endTop = end.top;
      const endBottom = end.bottom;
      const endLeft = end.left;

      this.selectionPlaceholder.style.display = 'block';
      this.selectionPlaceholder.style.top = `${startTop}px`;
      this.selectionPlaceholder.style.height = `${endBottom - startTop}px`;
      this.selectionPlaceholder.style.clipPath = `polygon(
        ${startLeft}px ${startHeight}px,
        ${startLeft}px 0,
        100% 0,
        100% ${endTop - startTop}px,
        ${endLeft}px ${endTop  - startTop}px,
        ${endLeft}px 100%,
        0 100%,
        0 ${startBottom - startTop}px
      )`;
    } else {
      this.selectionPlaceholder.style.display = 'none';
      this.selectionPlaceholder.style.clipPath = 'none';
      this.selectionPlaceholder.style.height = 0;
      this.selectionPlaceholder.style.top = 0;
    }
  }

  handleKeydown(event) {
    if (event.key === 'r' && this.isWriting) {
      return this.restartCurrentAction();
    }

    if (event.key === 'Escape') {
      return this.handleEscape();
    }

    if (event.metaKey && event.key === 'l') {
      return this.handleShortcut(event);
    }

    if (event.key !== 'Meta') {
      return this.handleKeyTyping();
    }
  }

  handleEscape() {
    if (this.isWriting) {
      return this.stopGenerate();
    }

    const { skipToolbarClose } = this.escapeToolbarHandler();

    if (skipToolbarClose) {
      this.quill.setSelection({
        index: this.currentRange.index,
        length: this.currentRange.length
      }, Quill.sources.SILENT);
    }

    if (this.isPopupOpened && !this.hasChanges && !skipToolbarClose) {
      this.hideToolbar();
    }
  }

  escapeToolbarHandler() {
    return (this.toolbarRef?.current && this.toolbarRef?.current.escapeToolbarHandler()) ?? {
      skipToolbarClose: false
    };
  }

  handleRestoreSelection() {
    this.setFakeSelection(this.selectionRange);
    this.currentRange = { ...this.selectionRange };
  }

  handleOptionClick(option) {
    if (option.expandToolbar) {
      this.expandPopup();
    }

    if (option.selectParagraph) {
      this.selectedText = this.getCurrentParagraphSelection();
    }

    return this.selectedText;
  }

  getSentenceRange(paragraphText, startIndex) {
    const getIndex = (reverse) => {
      let index = startIndex;
      let dotFound = false;

      while(dotFound) {
        reverse ? index-- : index++;
        dotFound = paragraphText[index] === '.' || index === 0 || index < paragraphText.length;
      }

      return index;
    }

    return {
      start: getIndex(true),
      end: getIndex()
    };
  }

  handleShortcut(event) {
    event && event.preventDefault();

    this.currentRange = this.quill.getSelection();
    
    if (this.currentRange) {
      if (this.isDisplayed) {
        this.hideToolbar();
      } else {
        this.showToolbar();
        store.dispatch(setIsShortcutOpen(true));
      }
    }
  }

  handleWidgetClick() {
    this.setupCurrentRange();

    if (this.currentRange) {
      this.quill.setSelection({ index: this.currentRange.index, length: this.currentRange.length });
      
      if (this.isDisplayed) {
        this.hideToolbar();
      } else {
        this.showToolbar();
        store.dispatch(setIsShortcutOpen(true));
      }
    }
  }

  async handleContractOverview() {
    this.setupCurrentRange();

    if (this.currentRange) {
      this.quill.setSelection({ index: this.currentRange.index, length: this.currentRange.length });
      if (!this.isDisplayed) {
        await this.showToolbar({ optionKey: 'contract-overview' });
      }
    }
  }

  setupCurrentRange() {
    this.currentRange = this.quill.getSelection();

    if (!this.currentRange) {
      this.currentRange = {
        index: 0,
        length: 0
      };
    }
  }

  handleInsertSuggestion({ suggestionText, bellow }) {
    if (this.currentRange) {
      let nextRange = {};

      const scrollY = window.scrollY;

      if (bellow) {
        this.quill.insertText(this.currentRange.index + this.currentRange.length, `\n${suggestionText}\n`, 'plain');
        nextRange = { index: this.currentRange.index + this.currentRange.length, length: suggestionText.length + 2};
        
      } else {
        this.quill.deleteText(this.currentRange.index, this.currentRange.length);
        this.quill.insertText(this.currentRange.index, suggestionText, 'plain');
        nextRange = { index: this.currentRange.index, length: suggestionText.length };
      }

      this.quill.setSelection(nextRange, Quill.sources.SILENT);
      window.scrollTo({ top: scrollY });
      this.currentRange = nextRange;

      this.hideToolbar();
    }
  }

  handleKeyTyping() {
    if (this.currentRange && this.currentRange.length && this.quill.hasFocus()) {
      this.hideToolbar();
    }
  }

  getCurrentParagraphSelection() {
    if (this.currentRange && !this.currentRange.length) {
      const [leaf, offset] = this.quill.getLeaf(this.currentRange.index);
      const pIndex = this.quill.getIndex(leaf);

      // this.quill.setSelection(pIndex, offset, Quill.sources.SILENT);

      this.setFakeSelection({
        index: pIndex,
        length: offset
      });

      this.currentRange = {
        index: pIndex,
        length: offset,
      };

      return this.quill.getText(pIndex, offset); 
    }

    return '';
  }

  restartCurrentAction() {
    store.dispatch(restartCurrentAction());
  }

  stopGenerate() {
    store.dispatch(stopGenerate());
  }

  closeAutocomplete() {
    store.dispatch(setAutocompleteIsOpen(false));
  }
}